import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlayCircle, Sparkles, Zap } from 'lucide-react';
import { NavBar } from '../components/NavBar';

/**
 * Full-page walkthrough — replaces the old inline dashboard embed so the video
 * gets a proper cinematic frame + immersive playback. Reached via the
 * "99-Second Walkthrough" button on the dashboard.
 */
const WalkthroughPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);

  const handlePlay = () => {
    setStarted(true);
    // Kick playback on the next tick so React swaps the poster overlay first
    setTimeout(() => {
      videoRef.current?.play?.().catch(() => {});
    }, 0);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="walkthrough-page">
      <NavBar title="Tutorial" />

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-6"
        >
          <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-3">
            <Sparkles className="h-3 w-3" /> Guided Tour
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wider leading-[1.05] bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">
            99-Second <span className="text-primary">Tutorial</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base max-w-2xl">
            See everything Ascend &amp; Surpass can do — in under two minutes.
          </p>
        </motion.div>

        {/* Cinematic video frame */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
        >
          {/* Ambient glows */}
          <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative aspect-video bg-black">
            <video
              ref={videoRef}
              src="/tutorial-preview.mp4"
              poster="/tutorial-poster.png"
              className="w-full h-full object-contain"
              controls={started}
              playsInline
              preload="metadata"
              data-testid="walkthrough-video"
            />

            {/* Play-to-start overlay — bigger tap target, keeps the poster
                looking premium instead of the default tiny play icon */}
            {!started && (
              <button
                type="button"
                onClick={handlePlay}
                className="absolute inset-0 flex items-center justify-center group bg-gradient-to-b from-transparent via-black/10 to-black/40"
                aria-label="Play walkthrough video"
                data-testid="walkthrough-play-overlay"
              >
                <span className="relative flex items-center justify-center">
                  <span className="absolute inset-0 -m-4 rounded-full bg-primary/30 blur-2xl group-hover:bg-primary/50 transition-all" />
                  <span className="relative h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-primary text-black flex items-center justify-center shadow-[0_10px_40px_rgba(250,204,21,0.5)] group-hover:scale-105 transition-transform">
                    <PlayCircle className="h-10 w-10 sm:h-12 sm:w-12" strokeWidth={1.8} />
                  </span>
                </span>
              </button>
            )}
          </div>
        </motion.div>

        {/* Footnote CTA */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-6 flex flex-wrap items-center justify-between gap-3"
        >
          <p className="text-xs sm:text-sm text-muted-foreground inline-flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-primary" />
            Ready to try it? Jump back into the app whenever you're set.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-4 py-2 shadow-[0_4px_14px_rgba(250,204,21,0.25)] transition-all"
            data-testid="walkthrough-go-dashboard-btn"
          >
            Take Me To My Dashboard
          </button>
        </motion.div>
      </main>
    </div>
  );
};

export default WalkthroughPage;
